import { FastForward } from "lucide-react";

const API_CONFIG = {
  //mindId: "f4b6b118-6301-422c-8fb1-6cacc6fe868d", //ORIGINAL
  // mindId: '5dffef22-a9cf-435c-9394-0fcad0d63cc1', // TEST
  mindId: 'ddc9e8a7-3bb0-4108-9ced-9752eaf13c05', // New MIND
  // shareKey: "5abfc53d20290041db5233ef4444464da78bbea4be0c74e142cd8f73079d4b12", // ORIGINAL
  //shareKey: 'bda656edf3b08b3a36187fcb91a0831b3352576e6b3ea23c8d3479183257bf77', // TEST MIND
  shareKey: "8c0eb4b941b4c08a49a2be8dcb48d5578f9fd1382276242e2a5f3c806c84b9af", // New MIND 
  //sessionId: "85050b603a3647f7bbc34babb748f3ed", //ORIGINAL
  // sessionId: '6ecc140a9bf449fe97db18e74717faa1', // 2nd test from original mind
  // sessionId: 'c4d26fec67a24e1da37c9e8e7bdbf720', // TEST MIND
  sessionId: '54e0c0a18b91406392327b98d89b562e', // New MIND
  baseUrl: "https://dev.paramai.studio:5012/",
  sseBaseUrl: "https://dev.paramai.studio:5013/",
  timeout: 30000,
  retryAttempts: 3,
  endpoints: {
    executeMind: "mindflow/execute_mind",
    sse: (jobId: string) => `events/${jobId}?check=1`,
    getSession: function (mindId: string, sessionId: string) {
      return `mindflow/${mindId}/${sessionId}/get_session`;
    },
  },
};

interface ExecuteMindResponse {
  status: string;
  job_id: string;
  session_id: string;
}

interface SSEMessage {
  message: string;
  status: string; // allow any string status, not just 'started' | 'pending' | 'completed'
  format: string;
  job_id: string;
  sessionData?: any; // Optional session data for completion event
}

interface ExecuteMindPayload {
  input_file?: File;
  input_files?: File[];
  mind_name?: string;
  is_default_ui_disabled?: boolean;
  args?: { files: string[] };
  session_id?: string;
  mind_id?: string;
  response_structure?: any;
}

/**
 * API Service for fetching session data
 */
class ApiService {
  private baseUrl: string;
  private sseBaseUrl: string;
  private mindId: string;
  private sessionId: string;
  private shareKey: string;
  private timeout: number;
  private retryAttempts: number;
  private currentJobId: string | null = null;
  private authToken: string | null = null;

  constructor() {
    this.baseUrl = API_CONFIG.baseUrl;
    this.sseBaseUrl = API_CONFIG.sseBaseUrl;
    this.mindId = API_CONFIG.mindId;
    this.sessionId = API_CONFIG.sessionId;
    this.shareKey = API_CONFIG.shareKey;
    this.timeout = API_CONFIG.timeout;
    this.retryAttempts = API_CONFIG.retryAttempts;
  }

  /**
   * Get default headers for API requests
   */
  private getDefaultHeaders(includeContentType = true) {
    const headers: Record<string, string> = {
      Accept: "application/json",
      "Accept-Language": "en-US,en;q=0.9",
      Origin: "https://dev.paramai.studio",
      Referer: "https://dev.paramai.studio/",
      "X-Client-Version": "1.0.0",
      "User-Agent":
        "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/138.0.0.0 Safari/537.36",
      "Content-Type": "application/json",
      "share-key": this.shareKey,
      "x-mind-id": this.mindId,
    };

    if (includeContentType) {
      headers["Content-Type"] = "application/json";
    }

    if (this.authToken) {
      headers["Authorization"] = `Bearer ${this.authToken}`;
    }

    if (this.shareKey) {
      headers["share-key"] = this.shareKey;
    }

    return headers;
  }

  /**
   * Build complete API URL
   */
  private buildUrl(endpoint: string, useSSE = false) {
    const base = useSSE ? this.sseBaseUrl : this.baseUrl;
    return `${base}${endpoint}`;
  }

  /**
   * Generic fetch with timeout and retry logic
   */
  private async fetchWithRetry(
    url: string,
    options: RequestInit = {},
    retries = this.retryAttempts
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response;
    } catch (error: any) {
      clearTimeout(timeoutId);

      if (error.name === "AbortError") {
        throw new Error("API request timeout - Please check your connection");
      }

      if (error.name === "TypeError") {
        throw new Error("Network error - Unable to reach API server");
      }

      if (error.message.includes("401")) {
        throw new Error(
          "API authentication failed - Invalid credentials or missing permissions"
        );
      }

      if (
        error.message.includes("500") ||
        error.message.includes("502") ||
        error.message.includes("503")
      ) {
        throw new Error("API server error - Please try again later");
      }

      if (
        retries > 0 &&
        (error.message.includes("fetch") || error.message.includes("Network"))
      ) {
        console.warn(
          `API request failed, retrying... (${retries} attempts left)`
        );
        await this.delay(1000);
        return this.fetchWithRetry(url, options, retries - 1);
      }

      throw error;
    }
  }

  /**
   * Utility method for delays
   */
  private delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Execute mind API call with file upload
   */
  async executeMind(
    filesOrFile: File[] | File,
    additionalData?: Partial<ExecuteMindPayload>
  ): Promise<ExecuteMindResponse> {
    try {
      const formData = new FormData();
      
      // Handle both single file and multiple files
      const files = Array.isArray(filesOrFile) ? filesOrFile : [filesOrFile];
      const fileArgs: string[] = [];

      // Add files to form data
      files.forEach((file, index) => {
        const fieldName = files.length === 1 ? "input_file" : `input_file`;
        formData.append(fieldName, file);
        fileArgs.push(fieldName);
      });

      // formData.append("input_file", file);
      formData.append("mind_id", this.mindId);
      formData.append("session_id", this.sessionId);
      formData.append("args", JSON.stringify({ files: ["input_file"] }));
      formData.append("session_name", "Cost Benchmarking Solution");

      const headers: Record<string, string> = {
        Accept: "application/json",
        "Accept-Language": "en-US,en;q=0.9",
        Origin: "https://dev.paramai.studio",
        Referer: "https://dev.paramai.studio/",
        "X-Client-Version": "1.0.0",
        "User-Agent":
          "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/138.0.0.0 Safari/537.36",
        "share-key": this.shareKey,
        "x-mind-id": this.mindId,
      };
      if (this.authToken) {
        headers["Authorization"] = `Bearer ${this.authToken}`;
      }
      // If X-Account-ID is set via setAccountId, it will be added by getDefaultHeaders, but add here if needed
      if ((this as any).accountId) {
        headers["X-Account-ID"] = (this as any).accountId;
      }
      // Remove Content-Type if present
      if (headers["Content-Type"]) {
        delete headers["Content-Type"];
      }
      const url = this.buildUrl(API_CONFIG.endpoints.executeMind, false);
      const response = await this.fetchWithRetry(url, {
        method: "POST",
        body: formData,
        headers: headers,
      });
      const data: ExecuteMindResponse = await response.json();
      if (data.status !== "success") {
        throw new Error(
          "Execute mind failed: " + (data as any).message || "Unknown error"
        );
      }
      this.currentJobId = data.job_id;
      return data;
    } catch (error: any) {
      console.error("API Error in executeMind:", error);
      throw new Error(`Failed to execute mind: ${error.message}`);
    }
  }

  /**
   * Listen to SSE events for job status
   */
  async listenToSSE(
    jobId: string,
    onMessage?: (message: SSEMessage) => void,
    onComplete?: () => void,
    onError?: (error: Error) => void,
    onSessionUpdate?: (sessionData: any) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = this.buildUrl(API_CONFIG.endpoints.sse(jobId), true);

      const eventSource = new EventSource(url);
      let lastHeartbeat = Date.now();
      let isCompleted = false; // Prevent double handling

      // Heartbeat check interval (every 30 seconds)
      const heartbeatInterval = setInterval(() => {
        const timeSinceLastMessage = Date.now() - lastHeartbeat;
        if (timeSinceLastMessage > 60000) {
          // 60 seconds without any message
          console.warn(
            "No heartbeat received for 60 seconds, but continuing..."
          );
        }
      }, 30000);

      eventSource.onopen = () => {
      };

      eventSource.onmessage = (event) => {
        lastHeartbeat = Date.now();

        try {
          let message: SSEMessage | null = null;
          if (event.data.startsWith("message")) {
            const jsonStr = event.data.substring(7);
            message = JSON.parse(jsonStr);
          } else if (event.data.trim().startsWith("{")) {
            // Handle plain JSON data events
            message = JSON.parse(event.data);
          }
          if (message) {
            if (onMessage) {
              onMessage(message);
            }
            // Check if completed
            if (message.status === "completed" && !isCompleted) {
              isCompleted = true;
              this.getSession()
                .then((sessionData) => {
                  if (onSessionUpdate) {
                    onSessionUpdate(sessionData);
                  }
                  if (onMessage) {
                    onMessage({ ...message, sessionData });
                  }
                  eventSource.close();
                  clearInterval(heartbeatInterval);
                  if (onComplete) {
                    onComplete();
                  }
                  resolve();
                })
                .catch((err) => {
                  console.error(
                    "[SSE DEBUG] Error fetching session data after completion:",
                    err
                  );
                  eventSource.close();
                  clearInterval(heartbeatInterval);
                  if (onComplete) {
                    onComplete();
                  }
                  resolve();
                });
            }
          } else {
          }
        } catch (error) {
          console.error(
            "[SSE DEBUG] Error parsing SSE message:",
            error,
            "Raw data:",
            event.data
          );
        }
      };

      eventSource.onerror = (error) => {
        console.error("SSE error:", error);
        clearInterval(heartbeatInterval);

        // Don't close on error - SSE will auto-reconnect
        // Only reject if we get a fatal error
        if (eventSource.readyState === EventSource.CLOSED) {
          eventSource.close();
          const err = new Error("SSE connection closed unexpectedly");
          if (onError) {
            onError(err);
          }
          reject(err);
        }
      };
    });
  }

  /**
   * Complete flow: Execute mind, listen to SSE, then get session
   */
  async executeAndWaitForCompletion(
    filesOrFile: File[] | File,
    additionalData?: Partial<ExecuteMindPayload>,
    onProgress?: (message: SSEMessage) => void,
    onSessionUpdate?: (sessionData: any) => void
  ) {
    try {
      const isMultiple = Array.isArray(filesOrFile);
      // Step 1: Execute mind
      const executeResponse = await this.executeMind(filesOrFile, additionalData);

      // Step 2: Listen to SSE until completion
      await this.listenToSSE(
        executeResponse.job_id,
        onProgress,
        () => console.log("SSE completed"),
        (error) => console.error("SSE error:", error),
        onSessionUpdate
      );

      // Do not call getSession again; rely on onSessionUpdate/sessionData from SSE completion
      return {
        executeResponse,
        sessionData: undefined, // or pass sessionData if you want, but don't fetch again
      };
    } catch (error: any) {
      console.error("Error in executeAndWaitForCompletion:", error);
      throw error;
    }
  }

  /**
   * Get session data from API
   */
  async getSession() {
    try {
      console.log("Fetching session data from API...");

      const endpoint = API_CONFIG.endpoints.getSession(
        this.mindId,
        this.sessionId
      );
      const url = this.buildUrl(endpoint);

      const response = await this.fetchWithRetry(url, {
        method: "GET",
        headers: this.getDefaultHeaders(),
      });

      const data = await response.json();

      if (!data) {
        throw new Error("Empty response from server");
      }

      return this.processSessionData(data);
    } catch (error: any) {
      console.error("API Error in getSession:", error);
      throw new Error(`Failed to fetch session data: ${error.message}`);
    }
  }

  /**
   * Process and validate session data
   */
  private processSessionData(rawData: any) {
    try {
      const sessionData = rawData.response ? rawData.response : rawData;
      if (!sessionData || !sessionData.output || !sessionData.output.content) {
        throw new Error("Invalid response structure: Missing required fields");
      }
      return sessionData;
    } catch (error: any) {
      console.error("Error processing session data:", error);
      throw new Error(`Data processing failed: ${error.message}`);
    }
  }

  /**
   * Health check for API
   */
  async healthCheck() {
    try {
      const response = await this.fetchWithRetry(this.baseUrl, {
        method: "HEAD",
      });
      return response.ok;
    } catch (error) {
      console.error("API Health check failed:", error);
      return false;
    }
  }

  /**
   * Get API configuration info
   */
  getApiInfo() {
    return {
      baseUrl: this.baseUrl,
      sseBaseUrl: this.sseBaseUrl,
      mindId: this.mindId,
      sessionId: this.sessionId,
      timeout: this.timeout,
      retryAttempts: this.retryAttempts,
      currentJobId: this.currentJobId,
    };
  }

  /**
   * Set authorization token
   */
  setAuthToken(token: string) {
    this.authToken = token;
  }

  /**
   * Set account ID (for X-Account-ID header)
   */
  setAccountId(accountId: string) {
    // Update getDefaultHeaders to include X-Account-ID
    const originalGetDefaultHeaders = this.getDefaultHeaders.bind(this);
    this.getDefaultHeaders = (includeContentType = true) => {
      const headers = originalGetDefaultHeaders(includeContentType);
      headers["X-Account-ID"] = accountId;
      return headers;
    };
  }
}

// Create and export singleton instance
const apiService = new ApiService();

// Export types
export type { ExecuteMindResponse, SSEMessage, ExecuteMindPayload };

// new update 

// Export individual methods for convenience
export const executeMind = (
  filesOrFile: File[] | File,
  additionalData?: Partial<ExecuteMindPayload>
) => apiService.executeMind(filesOrFile, additionalData);

export const listenToSSE = (
  jobId: string,
  onMessage?: (msg: SSEMessage) => void,
  onComplete?: () => void,
  onError?: (err: Error) => void
) => apiService.listenToSSE(jobId, onMessage, onComplete, onError);

// export const executeAndWaitForCompletion = (
//   file: File,
//   additionalData?: Partial<ExecuteMindPayload>,
//   onProgress?: (msg: SSEMessage) => void,
//   onSessionUpdate?: (sessionData: any) => void
// ) =>
//   apiService.executeAndWaitForCompletion(
//     file,
//     additionalData,
//     onProgress,
//     onSessionUpdate
//   );

export const executeAndWaitForCompletion = (
  filesOrFile: File[] | File,
  additionalData?: Partial<ExecuteMindPayload>,
  onProgress?: (msg: SSEMessage) => void,
  onSessionUpdate?: (sessionData: any) => void
) =>
  apiService.executeAndWaitForCompletion(
    filesOrFile,
    additionalData,
    onProgress,
    onSessionUpdate
  );

export const getSession = () => apiService.getSession();

export const healthCheck = () => apiService.healthCheck();

export const getApiInfo = () => apiService.getApiInfo();

export const setAuthToken = (token: string) => apiService.setAuthToken(token);

export const setAccountId = (accountId: string) =>
  apiService.setAccountId(accountId);

export default apiService;
